import { jsx, jsxs } from "react/jsx-runtime";
import React from "react";
import { Button, Classes } from "@blueprintjs/core";
import {
  connectToEditor,
  updateCircular,
  handleInverse,
  getShowGCContent
} from "../withEditorProps";
import "./style.css";
import { withHandlers, compose } from "recompose";
import { divideBy3 } from "../utils/proteinUtils";
import { getSelectionMessage } from "../utils/editorUtils";
import useMeltingTemp from "../utils/useMeltingTemp";
import MeltingTemp from "./MeltingTemp";
import {
  getSequenceWithinRange,
  getRangeLength,
  getOverlapsOfPotentiallyCircularRanges
} from "@teselagen/range-utils";
import { handleReadOnlyChange } from "../ToolBar/editTool";
import { TgHTMLSelect } from "@teselagen/ui";
export const EditReadOnlyItem = connectToEditor(({ readOnly }) => ({
  readOnly
}))((props) => {
  const {
    onSave,
    readOnly,
    showReadOnly,
    disableSetReadOnly,
    disableBpEditing
  } = props;
  const disabled = disableSetReadOnly || !onSave;
  return showReadOnly ? /* @__PURE__ */ jsx(StatusBarItem, { dataTest: "veStatusBar-readOnly", children: onSave ? /* @__PURE__ */ jsx(
    TgHTMLSelect,
    {
      "data-tip": !readOnly && typeof disableBpEditing === "string" ? disableBpEditing : void 0,
      options: [
        {
          label: "Read Only",
          value: "readOnly"
        },
        {
          label: "Editable" + (disableBpEditing ? "*" : ""),
          value: "editable"
        }
      ],
      disabled,
      className: Classes.MINIMAL + " veReadOnlySelect",
      value: readOnly ? "readOnly" : "editable",
      onChange: ({ target: { value } }) => handleReadOnlyChange(value === "readOnly", props)
    }
  ) : readOnly ? "Read Only" : "Editable" }) : null;
});
const ShowSelectionItem = compose(
  connectToEditor(
    ({ selectionLayer, caretPosition, sequenceData = { sequence: "" } }, ownProps, ...rest) => {
      return {
        showGCContent: getShowGCContent(rest[rest.length - 1], ownProps),
        selectionLayer,
        isProtein: sequenceData.isProtein,
        caretPosition,
        sequenceLength: sequenceData.sequence.length,
        sequenceData
      };
    }
  ),
  withHandlers({ handleInverse })
)(({
  selectionLayer = { start: -1, end: -1 },
  caretPosition = -1,
  sequenceLength = 0,
  isProtein,
  showAminoAcidUnitAsCodon,
  sequenceData = { sequence: "" },
  showGCContent,
  GCDecimalDigits,
  handleInverse: handleInverse2
}) => {
  const [showMeltingTemp] = useMeltingTemp();
  const sequence = getSequenceWithinRange(
    selectionLayer,
    sequenceData.sequence
  );
  return /* @__PURE__ */ jsxs(React.Fragment, { children: [
    /* @__PURE__ */ jsxs(StatusBarItem, { dataTest: "veStatusBar-selection", children: [
      getSelectionMessage({
        caretPosition,
        selectionLayer,
        sequenceLength,
        sequenceData,
        showGCContent,
        showAminoAcidUnitAsCodon,
        GCDecimalDigits,
        isProtein
      }),
      /* @__PURE__ */ jsx(
        Button,
        {
          minimal: true,
          disabled: sequenceLength <= 0,
          onClick: handleInverse2,
          style: { marginLeft: 5, color: "#48AFF0" },
          small: true,
          children: "Select Inverse"
        }
      )
    ] }),
    showMeltingTemp && /* @__PURE__ */ jsx(
      MeltingTemp,
      {
        WrapperToUse: StatusBarItem,
        sequence
      }
    )
  ] });
});
const ShowLengthItem = connectToEditor(
  ({
    sequenceData = { sequence: "" },
    selectionLayer = { start: -1, end: -1 }
  }) => ({
    sequenceLength: sequenceData.sequence.length,
    selectionLayer,
    sequenceData
  })
)(
  ({
    isProtein,
    sequenceLength = 0,
    showAminoAcidUnitAsCodon,
    selectionLayer = { start: -1, end: -1 },
    sequenceData = {}
  }) => {
    const isSelecting = selectionLayer.start > -1;
    const currentLength = isSelecting ? getRangeLength(selectionLayer, sequenceLength) : sequenceLength;
    if (isProtein) {
      return /* @__PURE__ */ jsx(StatusBarItem, { dataTest: "veStatusBar-length", children: `Length: ${divideBy3(currentLength, isProtein)} ${showAminoAcidUnitAsCodon ? "codons" : "AAs"}` });
    }
    const features = sequenceData.features ? Array.isArray(sequenceData.features) ? sequenceData.features : Object.values(sequenceData.features) : [];
    const targetRange = isSelecting ? selectionLayer : { start: 0, end: Math.max(0, sequenceLength - 1) };
    const hasCds = sequenceLength > 0 && features.some((feat) => {
      if (!feat || !feat.type || feat.type.toLowerCase() !== "cds") {
        return false;
      }
      const overlaps = getOverlapsOfPotentiallyCircularRanges(
        feat,
        targetRange,
        sequenceLength
      );
      return overlaps && overlaps.length > 0;
    });
    if (hasCds && currentLength > 0) {
      const remainder = currentLength % 3;
      const isMultipleOf3 = remainder === 0;
      const aaCount = isMultipleOf3 ? (currentLength / 3).toString() : (currentLength / 3).toFixed(1);
      return /* @__PURE__ */ jsxs(StatusBarItem, { dataTest: "veStatusBar-length", children: [
        `Length: ${currentLength} bps, ${aaCount} aa`,
        !isMultipleOf3 && /* @__PURE__ */ jsx("span", { style: { color: "red", marginLeft: "4px" }, children: ` (${remainder} extra bp in ORF)` })
      ] });
    }
    return /* @__PURE__ */ jsx(StatusBarItem, { dataTest: "veStatusBar-length", children: `Length: ${currentLength} bps` });
  }
);
const ShowTypeItem = connectToEditor(({ sequenceData }) => ({
  isProtein: sequenceData.isProtein,
  isOligo: sequenceData.isOligo,
  isRna: sequenceData.isRna,
  isMixedRnaAndDna: sequenceData.isMixedRnaAndDna
}))(({ isProtein, isOligo, isRna, isMixedRnaAndDna }) => {
  let type = "DNA";
  if (isProtein) type = "Protein";
  if (isRna) type = "RNA";
  if (isOligo) type = "Oligo";
  if (isMixedRnaAndDna) type = "Mixed RNA/DNA";
  return /* @__PURE__ */ jsx(StatusBarItem, { dataTest: "veStatusBar-type", children: type });
});
export const EditCircularityItem = compose(
  connectToEditor(
    ({
      readOnly,
      sequenceData,
      sequenceData: {
        circular
        /* materiallyAvailable */
      } = {}
    }) => ({
      readOnly,
      sequenceData,
      circular
    })
  ),
  withHandlers({ updateCircular })
)(({ readOnly, showCircularity, circular, updateCircular: updateCircular2 }) => {
  return showCircularity ? /* @__PURE__ */ jsx(StatusBarItem, { dataTest: "veStatusBar-circularity", children: readOnly ? circular ? "Circular" : "Linear" : /* @__PURE__ */ jsx(
    TgHTMLSelect,
    {
      onChange: ({ target: { value } }) => {
        updateCircular2(value === "circular");
      },
      className: Classes.MINIMAL,
      value: circular ? "circular" : "linear",
      options: [
        { label: "Circular", value: "circular" },
        { label: "Linear", value: "linear" }
      ]
    }
  ) }) : null;
});
export const EditAvailabilityItem = connectToEditor(
  ({ readOnly, sequenceData: { materiallyAvailable } = {} }) => ({
    readOnly,
    materiallyAvailable
  })
)(({ readOnly, showAvailability, materiallyAvailable, updateAvailability }) => {
  return showAvailability ? /* @__PURE__ */ jsx(StatusBarItem, { children: readOnly ? materiallyAvailable ? "Available" : "Unavailable" : /* @__PURE__ */ jsx(
    TgHTMLSelect,
    {
      onChange: ({ target: { value } }) => {
        updateAvailability(value === "available");
      },
      className: Classes.MINIMAL,
      value: materiallyAvailable ? "available" : "unavailable",
      options: [
        { label: "Available", value: "available" },
        { label: "Unavailable", value: "unavailable" }
      ]
    }
  ) }) : null;
});
export function StatusBar({
  disableSetReadOnly,
  disableBpEditing,
  onSave,
  editorName,
  showCircularity = true,
  showMoleculeType = true,
  showReadOnly = true,
  showAvailability = false,
  showGCContentByDefault,
  onSelectionOrCaretChanged,
  GCDecimalDigits = 1,
  isProtein,
  showAminoAcidUnitAsCodon,
  beforeReadOnlyChange
}) {
  return /* @__PURE__ */ jsxs("div", { className: "veStatusBar", children: [
    showMoleculeType && /* @__PURE__ */ jsx(ShowTypeItem, { editorName }),
    /* @__PURE__ */ jsx(
      EditReadOnlyItem,
      {
        beforeReadOnlyChange,
        editorName,
        onSave,
        disableBpEditing,
        disableSetReadOnly,
        showReadOnly
      }
    ),
    /* @__PURE__ */ jsx(
      EditCircularityItem,
      {
        editorName,
        showCircularity
      }
    ),
    /* @__PURE__ */ jsx(
      EditAvailabilityItem,
      {
        editorName,
        showAvailability
      }
    ),
    /* @__PURE__ */ jsx(
      ShowSelectionItem,
      {
        showAminoAcidUnitAsCodon,
        editorName,
        isProtein,
        showGCContentByDefault,
        onSelectionOrCaretChanged,
        GCDecimalDigits
      }
    ),
    /* @__PURE__ */ jsx(
      ShowLengthItem,
      {
        isProtein,
        editorName,
        showAminoAcidUnitAsCodon
      }
    )
  ] });
}
function StatusBarItem({ children, dataTest }) {
  return /* @__PURE__ */ jsxs(React.Fragment, { children: [
    /* @__PURE__ */ jsx("div", { "data-test": dataTest, className: "veStatusBarItem", children }),
    /* @__PURE__ */ jsx("div", { className: "veStatusBarSpacer" })
  ] });
}
export default StatusBar;
